import { useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ApiHttpError, exchangeOAuthCode } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect'
import { defaultLocale, isLocale } from '@/lib/locales'
import { FRONTEND_URL } from '@/lib/config'
import { oublier as oublierProvenance } from '@/lib/provenance'

export function OAuthCallbackPage() {
  const { t } = useTranslation()
  const { locale } = useParams<{ locale: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const setTokens = useAuthStore((s) => s.setTokens)
  const clearAuth = useAuthStore((s) => s.clear)
  const postLoginRedirect = usePostLoginRedirect()
  const startedRef = useRef(false)

  const preferredLocale =
    (typeof window !== 'undefined' && localStorage.getItem('preferred_locale')) ||
    locale ||
    defaultLocale
  const effectiveLocale = isLocale(preferredLocale) ? preferredLocale : defaultLocale

  const mutation = useMutation({
    mutationFn: async (code: string) => {
      const data = await exchangeOAuthCode(code)
      if (!data.success || !data.tokens?.access_token || !data.tokens.refresh_token) {
        throw new Error('Tokens manquants dans la réponse')
      }
      const tokens = data.tokens

      clearAuth()
      setTokens(tokens.access_token, tokens.refresh_token)

      // Persistance complémentaire (legacy keys)
      if (tokens.user_id) localStorage.setItem('callizy_user_id', String(tokens.user_id))
      if (tokens.email) localStorage.setItem('callizy_user_email', tokens.email)
      if (tokens.first_name) localStorage.setItem('callizy_user_first_name', tokens.first_name)
      if (tokens.last_name) localStorage.setItem('callizy_user_last_name', tokens.last_name)

      // The server stored the origin when it created the account (sent with
      // oauth-init) and gave it to PostHog with signup_completed.
      oublierProvenance()
      try {
        posthog.identify(
          String(tokens.user_id || tokens.email || ''),
          {
            email: tokens.email || '',
            name: `${tokens.first_name || ''} ${tokens.last_name || ''}`.trim(),
            auth_provider: 'google',
          },
        )
      } catch {
        // PostHog non initialisé
      }

      // Relay vers l'app Mac : si le flow a été lancé depuis Callizy.app
      // (qui a ouvert Safari pour bénéficier des cookies Google), on saute
      // le dashboard et on redirige vers /auth/desktop-handoff — Universal
      // Link qui réveille Callizy.app et lui transmet les tokens.
      if (sessionStorage.getItem('desktop_oauth_mode') === 'google') {
        sessionStorage.removeItem('desktop_oauth_mode')
        const handoffUrl =
          `${FRONTEND_URL}/auth/desktop-handoff` +
          `?token=${encodeURIComponent(tokens.access_token)}` +
          `&refresh=${encodeURIComponent(tokens.refresh_token)}`
        window.location.href = handoffUrl
        return
      }

      await postLoginRedirect(effectiveLocale)
      window.dispatchEvent(new Event('login-complete'))
    },
    onError: (error) => {
      console.error('[OAuthCallback]', error)
      const code =
        error instanceof ApiHttpError
          ? `oauth_${error.status}`
          : 'token_retrieval_failed'
      navigate(`/${effectiveLocale}/login?error=${code}`, { replace: true })
    },
  })

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const error = searchParams.get('error')
    const code = searchParams.get('code')

    if (error) {
      navigate(`/${effectiveLocale}/login?error=${encodeURIComponent(error)}`, { replace: true })
      return
    }
    if (!code) {
      navigate(`/${effectiveLocale}/login?error=missing_code`, { replace: true })
      return
    }

    // App NATIVE iOS : on ne consomme PAS le code ici. On le transmet à l'app
    // via le scheme custom ; elle l'échangera elle-même contre des JWT (keychain).
    // ASWebAuthenticationSession capte le `callizy://` et referme la feuille Safari.
    if (
      typeof window !== 'undefined' &&
      sessionStorage.getItem('native_oauth_mode') === 'google'
    ) {
      sessionStorage.removeItem('native_oauth_mode')
      window.location.href = `callizy://auth/native?code=${encodeURIComponent(code)}`
      return
    }

    mutation.mutate(code)
  }, [searchParams, navigate, effectiveLocale, mutation])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold">{t('oauthCallback.connecting', 'Connexion en cours…')}</h2>
        <p className="text-white/80 mt-2">
          {t('oauthCallback.subtitle', 'Finalisation sécurisée de votre authentification Google')}
        </p>
      </div>
    </div>
  )
}
